
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";

// Mixins
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { ISorter, IFilter } from "@/models/tables";
import { DefaultFilters } from "@/data/filters/webhooks";
import { DefaultSorters } from "@/data/sorters/webhooks";

// Types
import { IWebhook } from "@/models/webhooks";
import {
  IWebhooksProviderData,
  IWebhooksProviderMethods
} from "@/models/providers/webhooks";
import { AdminRoutes } from "@/data/enums/router";

@Component
export default class WebhooksProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: `WLP` /** [W]ebhooks [L]ist [P]rovider */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data

  public dataModule = DataModules.WEBHOOKS;
  public filters = this.availableFilters;
  public sorters = this.availableSorters;
  public isReloading = false;
  public isDeleting = false;

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.apiProps = { with_count: "subscriptions.hook_category.hooks" };
    this.getData();
  }

  // Methods

  public async reloadData() {
    try {
      this.isReloading = true;
      return this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  public viewWebhook(webhookId: IWebhook["id"]) {
    this.$router.push({
      name: AdminRoutes.WEBHOOKS_SETTINGS_WEBHOOK_EDIT,
      params: { webhookId }
    });
  }

  public async deleteWebhook(webhookId: IWebhook["id"]) {
    const modal = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          autoClose: false,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_confirmation", {
            object: this.$t("_.webhook")
              .toString()
              .toLowerCase()
          })
        },
        canCancel: [],
        events: {
          confirmed: async () => {
            try {
              this.isDeleting = true;

              await this.$store.dispatch(
                `data/${DataModules.WEBHOOKS}/remove`,
                { id: webhookId }
              );
              this.reloadData();

              this.$store.dispatch("toast/show", {
                message: this.$t(
                  "_sentence.webhooks.webhook_successfully_removed"
                )
              });
              modal.close();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isDeleting = false;
            }
          }
        }
      }
    });
  }

  public changeEnabled(webhookId: IWebhook["id"], enabled) {
    return this.$store.dispatch(`data/${DataModules.WEBHOOKS}/update`, {
      id: webhookId,
      data: { enabled }
    });
  }

  // Guarding

  public get canView() {
    return this.$userCan("get_webhook");
  }

  public get canUpdate() {
    return this.$userCan("update_webhook");
  }

  public get canDelete() {
    return this.$userCan("delete_webhook");
  }

  public guardFunctionalities = ["list_webhooks"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("webhooksProviderData")
  public providerData(): IWebhooksProviderData {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      canView: this.canView,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      guardFunctionalities: this.guardFunctionalities,
      hasRequestError: this.requestError,
      isDeleting: this.isDeleting,
      isLoading: this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      table: this.table,
      total: this.table.total,
      webhooks: this.table.data as IWebhook[]
    };
  }

  @Provide("webhooksProviderMethods")
  public get providerMethods(): IWebhooksProviderMethods {
    return {
      changeEnabled: this.changeEnabled,
      deleteWebhook: this.deleteWebhook,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      reloadData: this.reloadData,
      viewWebhook: this.viewWebhook
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $webhooksData: this.providerData(),
        $webhooksMethods: this.providerMethods
      })
    );
  }
}
